/**
 * CourseDeck — a translation family rendered as a stacked deck.
 *
 * Shown wherever a root course has ≥1 translation (Courses page). Stacked
 * layers behind signal "multiple versions" and fan out a little on hover; a
 * purple "N languages" chip summarises the family. Same card size/shape as a
 * singleton CourseCard. Clicking opens the deck-detail (handled by the parent).
 */
import { Check, Languages, Lock, MoreHorizontal } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { W } from '@/components/launch/launchWords'
import { groupThumbnail } from '@/lib/courseThumbnail'
import type { CourseGroup } from '@/lib/db'
import { spaLink } from '@/lib/spaLink'

/** The deck's clickable face: a real link when it has an address, a button otherwise. */
function Opener(props: React.ComponentProps<'a'> & React.ComponentProps<'button'>) {
  return props.href ? <a {...(props as React.ComponentProps<'a'>)} /> : <button {...(props as React.ComponentProps<'button'>)} />
}

export function CourseDeck({
  group, onClick, selectable = false, selected = false, onSelectedChange, href, privacy, onLaunch,
}: {
  /** Owner/Admins, published courses (S4): open the Launch flow. */
  onLaunch?: () => void
  /** Has a launch (S4) — a badge on the cover. */
  group: CourseGroup
  onClick: () => void
  /** Organisation courses only: shows a lock while the course is Private. */
  privacy?: 'private' | 'shared' | null
  /**
   * The course's own page (all its languages). A plain click still opens the
   * language picker; Cmd/Ctrl/middle-click or right-click → Open in new tab
   * opens this page in a new tab.
   */
  href?: string
  /** Bulk-select mode: the deck picks instead of opening. */
  selectable?: boolean
  selected?: boolean
  onSelectedChange?: (selected: boolean) => void
}) {
  const { root, all } = group
  const count = all.length

  return (
    <div className="group relative w-full">
      {/* Stacked layers behind, offset to read as a deck — fan out on hover. */}
      <div className="absolute -bottom-1.5 left-2 right-2 h-full rounded-xl bg-purple-100 border border-purple-200 transition-transform duration-200 group-hover:translate-y-1.5" />
      <div className="absolute -bottom-0.5 left-1 right-1 h-full rounded-xl bg-purple-50 border border-purple-200 transition-transform duration-200 group-hover:translate-y-1" />

      <Opener
        {...(href && !selectable
          ? spaLink(href, onClick)
          : { type: 'button' as const, onClick: () => { if (selectable) onSelectedChange?.(!selected); else onClick() } })}
        className={[
          'relative block w-full overflow-hidden rounded-xl border bg-white text-left shadow-sm transition-all duration-200 group-hover:shadow-md',
          selected ? 'border-[#9333EA] ring-2 ring-[#9333EA]/30' : 'border-gray-200 group-hover:border-gray-300 group-hover:-translate-y-1',
        ].join(' ')}
      >
        {/* Cover */}
        <div className="aspect-video relative overflow-hidden">
          <img
            src={groupThumbnail(group)}
            alt={root.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {privacy === 'private' && (
            <span title="Private — only its creator can see it"
              className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-gray-900/70 backdrop-blur-sm text-[10px] font-semibold text-white">
              <Lock className="w-3 h-3" /> Private
            </span>
          )}
          {selectable && (
            <span aria-hidden className={[
              'absolute top-2 right-2 grid h-6 w-6 place-items-center rounded-full border-2 transition-colors',
              selected ? 'border-[#9333EA] bg-[#9333EA] text-white' : 'border-white/80 bg-black/30 text-transparent',
            ].join(' ')}>
              <Check className="h-3.5 w-3.5" />
            </span>
          )}
        </div>

        {/* Meta — title + "N languages" chip */}
        <div className="p-4">
          <h3 title={root.title || 'Untitled Course'}
            className="min-h-[2.5rem] text-sm font-medium leading-snug text-gray-900 line-clamp-2">
            {root.title || 'Untitled Course'}
          </h3>
          <div className="mt-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-purple-100 text-[#9333EA]">
              <Languages className="w-3 h-3" /> {count} languages
            </span>
          </div>
        </div>
      </Opener>
      {/* Outside the link: a button can't sit inside an <a>. */}
      {onLaunch && !selectable && (
        <DropdownMenu>
          <DropdownMenuTrigger aria-label="More"
            className="absolute bottom-3 right-3 z-[2] flex h-7 w-7 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-gray-100 group-hover:opacity-100 focus-visible:opacity-100">
            <MoreHorizontal className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onLaunch}>{W.Launch}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
